/**
 * ROUND 13 G7 -- one wording for one tolerance state. Ninth round.
 *
 * Two wordings for the unsigned state and four for the signed one
 * described a single declaration, across six surfaces. Four of those
 * six already read the same sentence, because they render
 * `ToleranceBadge` and the badge composes it from `toleranceState.*`.
 * The other two -- the owner's declaration form and the incident
 * strip -- each wrote their own.
 *
 * So the sentence moves out of the badge into a function both can
 * call. One wording because one function, rather than one wording
 * because three files currently agree: the previous eight rounds of
 * this finding were each closed by making the files agree again, and
 * each time a ninth surface was written that did not know.
 *
 * Pure and t-injected: the badge is a component, these two callers are
 * a form and a strip, and a shared React component would drag layout
 * into places that need a string.
 */
import type { TFunction } from "i18next";

import type { ToleranceStateRow } from "shared/tolerance/useToleranceState";
import { roleLabel } from "shared/vocab/workflowState";
import { i18n } from "shared/i18n";
import { formatDate, formatTime } from "shared/time/formatDate";

/** Who a pending declaration waits on, in the reader's words. */
const AWAITING: Record<string, string> = {
  cro: "toleranceState.awaitingCro",
  business_owner: "toleranceState.awaitingBusinessOwner",
};

/** ROUND 15 G2 -- who holds the seats, when a surface has asked. */
export interface SeatHolderMap {
  holders?: Record<string, string[]>;
}

/**
 * Who a pending declaration waits on, in the reader's words -- and,
 * when the holders are known, by name: "awaiting Sara Al-Hassan (Chief
 * Risk Officer) since 13:50". A seat nobody holds says so rather than
 * naming a role as though somebody were behind it; a seat several hold
 * says how many, because naming one of three sends the reader to chase
 * the wrong person.
 */
export function awaitingPhrase(
  t: TFunction,
  awaitingRole: string | null | undefined,
  opts: {
    holders?: string[] | undefined;
    since?: string | null | undefined;
    /** Round 16 G14 -- false when the service has no recorded owner. */
    ownerRecorded?: boolean | undefined;
    /** Round 20 W3 -- the owner the register names, seated or not. */
    ownerName?: string | null | undefined;
  } = {},
): string {
  /* Round 16 G14 -- a pending tolerance on a service with no recorded
     owner stayed pending while the band promised an owner. Only the
     owner can countersign, so it is waiting on nobody until one is. */
  if (awaitingRole === "business_owner" && opts.ownerRecorded === false) {
    if (opts.ownerName)
      return t("toleranceState.awaitingOwnerNotSeat", { owner: opts.ownerName });
    return t("toleranceState.awaitingNoOwner");
  }
  const roleKey = AWAITING[awaitingRole ?? ""] ?? "toleranceState.awaitingSignature";
  if (!awaitingRole || opts.holders === undefined) return t(roleKey);
  const role = roleLabel(awaitingRole);
  if (opts.holders.length === 0) {
    return t("toleranceState.awaitingUnheld", { role });
  }
  if (opts.holders.length > 1) {
    return t("toleranceState.awaitingSeveral", { role, count: opts.holders.length });
  }
  const who = opts.holders[0];
  return opts.since
    ? t("toleranceState.awaitingNamedSince", {
        who,
        role,
        since: formatWhen(opts.since),
      })
    : t("toleranceState.awaitingNamed", { who, role });
}

/** "13:50 today" reads as a clock; an older wait reads as a date.
 *  Round 16 G14 -- in the product's formats, not the browser's: the
 *  browser's default gave "since 07:07 PM" and "since Sep 18" beside a
 *  24-hour clock and day-month dates everywhere else. */
function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay ? formatTime(d, i18n.language) : formatDate(d, i18n.language);
}

/**
 * The tolerance state, said once.
 *
 * Returns null where the row says nothing we can read -- not knowing
 * is not the same as "undeclared", and asserting an absence we have
 * not confirmed is the mistake this whole vocabulary exists to avoid.
 */
export function toleranceSentence(
  t: TFunction,
  state: ToleranceStateRow | undefined,
  seats?: SeatHolderMap,
): string | null {
  if (!state) return null;
  const awaiting = awaitingPhrase(t, state.awaiting_role, {
    // Round 17 G19 -- the owner's leg is this service's recorded owner,
    // named whether or not the seat list has loaded.
    holders:
      state.awaiting_role === "business_owner" && state.owner_name
        ? [state.owner_name]
        : seats?.holders && state.awaiting_role
          ? (seats.holders[state.awaiting_role] ?? [])
          : undefined,
    since: state.awaiting_since,
    ownerRecorded: state.owner_recorded,
    ownerName: state.owner_name,
  });
  /* The distinction the old two words carried is real -- one number is
     in force, the other is proposed -- but it is not the distinction
     they announced, and the reader's question is the same either way:
     who has to sign this.

     Where the proposal would REPLACE the figure rather than confirm
     it, that is a third fact and keeps its own sentence: collapsing it
     would put a number on screen that no verdict is using. */
  const inFlight = state.proposed_hours !== null && state.proposed_hours !== undefined;
  switch (state.status) {
    case "undeclared":
      return t("toleranceState.undeclared");
    case "pending":
      return t("toleranceState.pending", { hours: state.proposed_hours, awaiting });
    case "signed":
      return (
        t("toleranceState.signed", { hours: state.hours }) +
        lapsedPhrase(t, state.signer_seat_lapsed ?? [])
      );
    case "set_unsigned":
      /* Round 16 D21 -- a figure in force that nobody signed for. It
         read "2h declared, awaiting signature" where nothing had been
         declared at all (the column was seeded), while the chip said
         "2h" and the war room counted down to a breach against it. One
         wording, on every surface that names it: in force, not signed.
         A declaration in flight is said after it. */
      return inFlight
        ? t("toleranceState.unsignedDeclared", {
            hours: state.hours,
            proposed: state.proposed_hours,
            awaiting,
          })
        : t("toleranceState.unsigned", { hours: state.hours });
    default:
      // A status outside the four: falling through to "not yet signed"
      // would claim something about a row we cannot read.
      return null;
  }
}

/** Round 16 G14 -- the state as a sentence, for a surface that writes
 *  another sentence after it. The badge phrase ("2h declared and
 *  signed") carries no full stop, and "Signed by ..." or "Breach at
 *  ..." written straight after it read as one run-on line. */
export function asSentence(phrase: string | null | undefined): string {
  const p = (phrase ?? "").trim();
  if (!p) return "";
  const capital = p.charAt(0).toLocaleUpperCase() + p.slice(1);
  return /[.!?]$/.test(capital) ? capital : `${capital}.`;
}

/** Round 20 V2 -- a signature whose signer does not hold the seat today:
 *  "signed by Nora Al-Qahtani as CRO; not a holder of the seat today".
 *  Where the seat held at signing is on the record, it reads "the CRO
 *  at the time". */
export function lapsedPhrase(
  t: TFunction,
  lapsed: { leg: string; name: string | null; seat_at_signing: string | null }[],
): string {
  return lapsed
    .map(
      (l) =>
        " " +
        t(
          l.seat_at_signing === l.leg
            ? "toleranceState.signerLapsedAtTheTime"
            : "toleranceState.signerLapsed",
          { name: l.name || "—", role: roleLabel(l.leg) },
        ),
    )
    .join("");
}
